import { useEffect, useState, useRef } from "react";
import { Badge, Col, Row, Button, Card, Form } from "react-bootstrap";
import MarketingContactsListing from "../../../Components/MarketingContactsListing";
import {
  getAllGroupMetadata,
  fetchContactMetadata,
  saveGroupContact,
} from "../../../Services/MarketingContacts";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import * as XLSXStyle from "xlsx-js-style";
import { FaUpload, FaDownload } from "react-icons/fa";
import { getEmailStatus } from "../../../utils/emailStatus";
import {
  formatSubContact,
  getSubContacts,
  subContactSearchFields,
} from "../../../utils/subContacts";

// Sort engine: field registry + typed comparators + curated presets.
// Adding a field here makes it sortable everywhere.
const SORT_FIELDS = {
  group_name: { label: "Group Name", type: "string" },
  contact_name: { label: "Contact Name", type: "string" },
  contact_email: { label: "Contact Email", type: "string" },
  city: { label: "City", type: "string" },
  state: { label: "State", type: "string" },
  org_type: { label: "Org Type", type: "string" },
  avg_attend: { label: "Avg Attend", type: "number" },
  denom: { label: "Denomination", type: "string" },
  // Free text, but usually a number: compare digits numerically so "2" < "10"
  chatgpt_rank: { label: "ChatGPT Rank", type: "string", numeric: true },
  email_sent: { label: "Email Sent", type: "bool" },
  live_contact: {
    label: "Live Contact",
    type: "bool",
    accessor: (c) => c.live_contact !== false,
  },
  phone1: { label: "Phone 1", type: "string" },
  phone2: { label: "Phone 2", type: "string" },
  view_count_direct: {
    label: "Prospect Views",
    type: "number",
    accessor: (c) =>
      c.view_count_direct !== undefined ? c.view_count_direct : c.view_count,
  },
  last_viewed_direct: { label: "Prospect Last Viewed", type: "date" },
  // Admin views are still tracked and editable, they just no longer have a
  // column in the table; keep the sort field so saved/manual sorts still work.
  view_count_admin: { label: "Admin Views", type: "number" },
  date_sent: { label: "Most Recent Date Sent", type: "date" },
  date_added: { label: "Date Added", type: "date" },
  follow_up_date: { label: "Follow-up", type: "date" },
};

const compareByField = (a, b, field, dir) => {
  const meta = SORT_FIELDS[field];
  if (!meta) return 0;
  const accessor = meta.accessor || ((c) => c[field]);
  const va = accessor(a);
  const vb = accessor(b);
  const sign = dir === "desc" ? -1 : 1;

  switch (meta.type) {
    case "string":
      return (
        sign *
        String(va || "").localeCompare(String(vb || ""), undefined, {
          sensitivity: "base",
          numeric: !!meta.numeric,
        })
      );
    case "number": {
      const na = Number(va);
      const nb = Number(vb);
      const naOk =
        va !== null && va !== undefined && va !== "" && !Number.isNaN(na);
      const nbOk =
        vb !== null && vb !== undefined && vb !== "" && !Number.isNaN(nb);
      if (!naOk && nbOk) return 1;
      if (naOk && !nbOk) return -1;
      if (!naOk && !nbOk) return 0;
      return sign * (na - nb);
    }
    case "date": {
      const ta = Date.parse(va);
      const tb = Date.parse(vb);
      const taOk = !Number.isNaN(ta);
      const tbOk = !Number.isNaN(tb);
      if (!taOk && tbOk) return 1;
      if (taOk && !tbOk) return -1;
      if (!taOk && !tbOk) return 0;
      return sign * (ta - tb);
    }
    case "bool":
      return sign * (Number(!!va) - Number(!!vb));
    default:
      return 0;
  }
};

const chainCompare = (sortKeys) => (a, b) => {
  for (const { field, dir } of sortKeys) {
    const cmp = compareByField(a, b, field, dir);
    if (cmp !== 0) return cmp;
  }
  return 0;
};

const SORT_PRESETS = [
  {
    id: "group_name_asc",
    label: "Group Name (A–Z)",
    keys: [{ field: "group_name", dir: "asc" }],
  },
  // {
  //   id: "contact_name_asc",
  //   label: "Contact Name (A–Z)",
  //   keys: [{ field: "contact_name", dir: "asc" }],
  // },
  // {
  //   id: "contact_email_asc",
  //   label: "Contact Email (A–Z)",
  //   keys: [{ field: "contact_email", dir: "asc" }],
  // },
  {
    id: "recent_sent_then_views",
    label: "Most Recent Sent, then Prospect Views",
    keys: [
      { field: "date_sent", dir: "desc" },
      { field: "view_count_direct", dir: "desc" },
    ],
  },
  {
    id: "never_sent",
    label: "Not Sent",
    keys: [
      { field: "email_sent", dir: "asc" }
    ],
  },
  // {
  //   id: "email_sent_newest",
  //   label: "Email Sent first, newest first",
  //   keys: [
  //     { field: "email_sent", dir: "desc" },
  //     { field: "date_sent", dir: "desc" },
  //   ],
  // },
  // {
  //   id: "live_by_views",
  //   label: "Live Contacts by Views",
  //   keys: [
  //     { field: "live_contact", dir: "desc" },
  //     { field: "view_count_direct", dir: "desc" },
  //   ],
  // },
  {
    id: "date_sent_desc",
    label: "Most Recent Date Sent (newest first)",
    keys: [{ field: "date_sent", dir: "desc" }],
  },
  {
    id: "date_sent_asc",
    label: "Most Recent Date Sent (oldest first)",
    keys: [{ field: "date_sent", dir: "asc" }],
  },
  {
    id: "views_desc",
    label: "Prospect Views (high to low)",
    keys: [{ field: "view_count_direct", dir: "desc" }],
  },
  {
    id: "views_asc",
    label: "Prospect Views (low to high)",
    keys: [{ field: "view_count_direct", dir: "asc" }],
  },
];

const DEFAULT_SORT_KEYS = SORT_PRESETS[0].keys;

const findMatchingPresetId = (sortKeys) => {
  const match = SORT_PRESETS.find(
    (p) =>
      p.keys.length === sortKeys.length &&
      p.keys.every(
        (k, i) =>
          k.field === sortKeys[i].field && k.dir === sortKeys[i].dir
      )
  );
  return match ? match.id : "";
};

const MarketingContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [emailsSent, setEmailsSent] = useState(0);
  const [emailsPending, setEmailsPending] = useState(0);
  const [emailProblems, setEmailProblems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKeys, setSortKeys] = useState(DEFAULT_SORT_KEYS);
  const [showAddSort, setShowAddSort] = useState(false);
  const [addSortField, setAddSortField] = useState("");
  const [addSortDir, setAddSortDir] = useState("asc");
  const [refreshTable, setRefreshTable] = useState(true);
  const [showSpinner, setShowSpinner] = useState(true);
  const listingRef = useRef(null);

  // Header-click sorter: shift-click appends / toggles; plain click replaces
  const handleHeaderSortClick = (field, shiftKey) => {
    setSortKeys((prev) => {
      const idx = prev.findIndex((k) => k.field === field);
      if (shiftKey) {
        if (idx === -1) return [...prev, { field, dir: "asc" }];
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          dir: next[idx].dir === "asc" ? "desc" : "asc",
        };
        return next;
      }
      if (prev.length === 1 && idx === 0) {
        return [{ field, dir: prev[0].dir === "asc" ? "desc" : "asc" }];
      }
      return [{ field, dir: "asc" }];
    });
  };

  const availableFieldsToAdd = Object.entries(SORT_FIELDS).filter(
    ([f]) => !sortKeys.some((k) => k.field === f)
  );

  const handleConfirmAddSort = () => {
    if (!addSortField) return;
    setSortKeys((prev) => [
      ...prev,
      { field: addSortField, dir: addSortDir },
    ]);
    setAddSortField("");
    setAddSortDir("asc");
    setShowAddSort(false);
  };
  const uploadInputRef = useRef(null);

  useEffect(() => {
    async function loadContacts() {
      setShowSpinner(true);
      try {
        // Get all metadata files
        const metadataResponse = await getAllGroupMetadata();
        const metadataFiles = metadataResponse.metadata_files || [];

        // Filter only contact files (exclude directories)
        const contactFiles = metadataFiles.filter(
          (file) =>
            file.includes("contacts/") &&
            file.endsWith(".json") &&
            !file.endsWith("/")
        );

        // Fetch each contact file
        const contactPromises = contactFiles.map(async (file) => {
          const fileUrl = `https://elqmq.upcloudobjects.com/demo-videos/${file}`;
          try {
            return await fetchContactMetadata(fileUrl);
          } catch (error) {
            console.error(`Error fetching contact file ${file}:`, error);
            return null;
          }
        });

        const contactResults = await Promise.all(contactPromises);
        const validContacts = contactResults.filter(
          (contact) => contact !== null
        );

        setContacts(validContacts);
        setTotalContacts(validContacts.length);

        // Calculate email stats
        const sentCount = validContacts.filter(
          (contact) => contact.email_sent === true
        ).length;
        const pendingCount = validContacts.filter(
          (contact) => contact.email_sent === false
        ).length;
        setEmailsSent(sentCount);
        setEmailsPending(pendingCount);
        setEmailProblems(
          validContacts.filter((contact) => getEmailStatus(contact).problem)
            .length
        );
      } catch (error) {
        console.error("Error loading contacts:", error);
        setContacts([]);
        setTotalContacts(0);
      } finally {
        setShowSpinner(false);
      }
    }

    loadContacts();
  }, [refreshTable]);

  // Filter + sort contacts whenever inputs change
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = !term
      ? [...contacts]
      : contacts.filter((contact) => {
        const fields = [
          contact.group_name,
          contact.contact_name,
          contact.contact_email,
          contact.notes,
          getEmailStatus(contact).label,
          contact.contact_title,
          ...subContactSearchFields(contact),
        ];
        return fields.some(
          (field) => field && field.toLowerCase().includes(term)
        );
      });

    const effectiveKeys = sortKeys.length > 0 ? sortKeys : DEFAULT_SORT_KEYS;
    filtered.sort(chainCompare(effectiveKeys));
    setFilteredContacts(filtered);
  }, [contacts, searchTerm, sortKeys]);

  const refreshTableHandler = () => {
    setShowSpinner(true);
    setRefreshTable((prev) => !prev);
  };

  const handleAddNewContact = () => {
    if (listingRef.current) {
      listingRef.current();
    }
  };

  const normalizeHeader = (header) => {
    return String(header || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const getMappedValue = (row, keys) => {
    for (let i = 0; i < keys.length; i += 1) {
      const value = row[keys[i]];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
    return "";
  };

  const parseEmailSent = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return ["true", "1", "yes", "y", "sent"].includes(normalized);
  };

  const mapCsvRowToContact = (row) => {
    const normalizedRow = Object.keys(row || {}).reduce((acc, key) => {
      acc[normalizeHeader(key)] = row[key];
      return acc;
    }, {});

    const groupName = getMappedValue(normalizedRow, [
      "groupname",
      "group_name",
      "organization",
      "organizationname",
      "group",
    ]);
    const contactName = getMappedValue(normalizedRow, [
      "contactname",
      "contact_name",
      "name",
    ]);
    const contactEmail = getMappedValue(normalizedRow, [
      "contactemail",
      "contact_email",
      "email",
    ]);

    if (!groupName || !contactName || !contactEmail) {
      return null;
    }

    return {
      group_name: groupName,
      contact_name: contactName,
      contact_email: contactEmail,
      city: getMappedValue(normalizedRow, ["city"]),
      state: getMappedValue(normalizedRow, ["state"]),
      avg_attend: getMappedValue(normalizedRow, ["avgattend", "avg_attend"]),
      denom: getMappedValue(normalizedRow, ["denom", "denomination"]),
      group_name_pronunciation: getMappedValue(normalizedRow, [
        "groupnamepronunciation",
        "group_name_pronunciation",
      ]),
      org_type: getMappedValue(normalizedRow, ["orgtype", "org_type"]),
      demo_id: getMappedValue(normalizedRow, ["demoid", "demo_id"]),
      phone1: getMappedValue(normalizedRow, ["phone1", "phone_1", "phoneone"]),
      phone1_type: getMappedValue(normalizedRow, [
        "phone1type",
        "phone_1_type",
        "phoneonetype",
      ]),
      phone2: getMappedValue(normalizedRow, ["phone2", "phone_2", "phonetwo"]),
      phone2_type: getMappedValue(normalizedRow, [
        "phone2type",
        "phone_2_type",
        "phonetwotype",
      ]),
      notes: getMappedValue(normalizedRow, ["notes"]),
      email_sent: parseEmailSent(
        getMappedValue(normalizedRow, ["emailsent", "email_sent"])
      ),
      date_added: getMappedValue(normalizedRow, ["dateadded", "date_added"]),
    };
  };

  const handleUploadButtonClick = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
      uploadInputRef.current.click();
    }
  };

  const handleExportToExcel = () => {
    if (!filteredContacts.length) {
      toast.info("No contacts to export.");
      return;
    }
    // Column definitions: label shown in the header, type governs cell coercion + width
    const columns = [
      { key: "group_name", label: "Group Name", type: "string", width: 30 },
      { key: "contact_name", label: "Contact Name", type: "string", width: 24 },
      { key: "contact_email", label: "Contact Email", type: "string", width: 32 },
      { key: "contact_title", label: "Contact Role / Title", type: "string", width: 22 },
      { key: "phone1", label: "Phone 1", type: "string", width: 16 },
      { key: "phone1_type", label: "Phone 1 Type", type: "string", width: 14 },
      { key: "phone2", label: "Phone 2", type: "string", width: 16 },
      { key: "phone2_type", label: "Phone 2 Type", type: "string", width: 14 },
      { key: "city", label: "City", type: "string", width: 18 },
      { key: "state", label: "State", type: "string", width: 10 },
      { key: "org_type", label: "Org Type", type: "string", width: 14 },
      { key: "denom", label: "Denomination", type: "string", width: 16 },
      { key: "chatgpt_rank", label: "ChatGPT Rank", type: "string", width: 14 },
      { key: "avg_attend", label: "Avg Attend", type: "number", width: 12 },
      { key: "group_name_pronunciation", label: "Group Name Pronunciation", type: "string", width: 26 },
      { key: "demo_id", label: "Demo ID", type: "string", width: 20 },
      { key: "email_sent", label: "Email Sent", type: "bool", width: 12 },
      { key: "date_sent", label: "Most Recent Date Sent", type: "date", width: 22 },
      { key: "email_status_label", label: "Email Status", type: "string", width: 18 },
      { key: "email_status_detail", label: "Email Status Detail", type: "string", width: 40 },
      { key: "email_status_date", label: "Email Status Date", type: "date", width: 20 },
      { key: "date_added", label: "Date Added", type: "date", width: 20 },
      { key: "view_count_direct", label: "Prospect Views", type: "number", width: 14 },
      { key: "view_count_admin", label: "Admin Views", type: "number", width: 12 },
      { key: "live_contact", label: "Live Contact", type: "bool", width: 12 },
      { key: "other_contacts", label: "Other Contacts", type: "string", width: 60 },
      { key: "notes", label: "Notes", type: "string", width: 50 },
    ];

    // Build AOA (array of arrays): header row + data rows with proper cell types
    const headerRow = columns.map((c) => c.label);
    const dataRows = filteredContacts.map((contact) =>
      columns.map((col) => {
        const raw =
          col.key === "email_status_label"
            ? getEmailStatus(contact).label
            : col.key === "other_contacts"
              ? getSubContacts(contact).map(formatSubContact).join("; ")
              : contact[col.key];
        if (raw === undefined || raw === null || raw === "") return null;
        if (col.type === "number") {
          const n = Number(raw);
          return Number.isNaN(n) ? raw : n;
        }
        if (col.type === "bool") {
          if (col.key === "live_contact") return raw !== false;
          return !!raw;
        }
        if (col.type === "date") {
          const d = new Date(raw);
          return Number.isNaN(d.getTime()) ? raw : d;
        }
        return String(raw);
      })
    );
    const aoa = [headerRow, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

    // Assign display formats for typed cells (dates, numbers, bools)
    const dateFmt = "mm-dd-yyyy";
    const numFmt = "#,##0";
    const cellBorder = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };
    const headerBorder = {
      top: { style: "thin", color: { rgb: "1E7E34" } },
      bottom: { style: "thin", color: { rgb: "1E7E34" } },
      left: { style: "thin", color: { rgb: "1E7E34" } },
      right: { style: "thin", color: { rgb: "1E7E34" } },
    };
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { patternType: "solid", fgColor: { rgb: "28A745" } },
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: headerBorder,
    };
    // Style the header row
    columns.forEach((_, cIdx) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c: cIdx });
      const cell = worksheet[addr];
      if (cell) cell.s = headerStyle;
    });
    // Style + format data rows
    for (let r = 0; r < dataRows.length; r++) {
      columns.forEach((col, cIdx) => {
        const addr = XLSX.utils.encode_cell({ r: r + 1, c: cIdx });
        const cell = worksheet[addr];
        if (!cell) return;
        if (col.type === "date") cell.z = dateFmt;
        else if (col.type === "number") cell.z = numFmt;
        else if (col.type === "bool") {
          // Render as "Yes" / "No" for readability
          cell.v = cell.v ? "Yes" : "No";
          cell.t = "s";
        }
        cell.s = {
          border: cellBorder,
          alignment: { vertical: "top", wrapText: col.key === "notes" },
        };
      });
    }
    // Taller header row so bold text is easy to read
    worksheet["!rows"] = [{ hpt: 22 }];

    // Column widths
    worksheet["!cols"] = columns.map((c) => ({ wch: c.width }));

    // Freeze the header row
    worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };
    worksheet["!views"] = [{ state: "frozen", ySplit: 1 }];

    // Autofilter across all header cells
    const lastCol = XLSX.utils.encode_col(columns.length - 1);
    worksheet["!autofilter"] = { ref: `A1:${lastCol}${dataRows.length + 1}` };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Marketing Contacts");
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    // Use the styled writer so cell.s properties are preserved
    XLSXStyle.writeFile(
      workbook,
      `marketing_contacts_${yyyy}-${mm}-${dd}.xlsx`
    );
  };

  const handleDownloadCsvTemplate = () => {
    const headers = [
      "group_name",
      "contact_name",
      "contact_email",
      "city",
      "state",
      "avg_attend",
      "denom",
      "group_name_pronunciation",
      "org_type",
      "demo_id",
      "phone1",
      "phone1_type",
      "phone2",
      "phone2_type",
      "notes",
      "email_sent",
      "date_added",
    ];
    const sampleRow = [
      "Acme Church",
      "Jane Doe",
      "jane.doe@example.com",
      "Dallas",
      "TX",
      "250",
      "Baptist",
      "Ak-mee Church",
      "Church",
      "",
      "555-123-4567",
      "Direct Number",
      "555-987-6543",
      "Company Number",
      "Initial outreach contact",
      "false",
      "",
    ];

    const csvContent = [headers.join(","), sampleRow.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "marketing_contacts_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    setShowSpinner(true);
    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: "array" });
      const firstSheet = workbook.SheetNames[0];

      if (!firstSheet) {
        throw new Error("No worksheet found in uploaded file.");
      }

      const worksheet = workbook.Sheets[firstSheet];
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: false,
      });

      if (!rows.length) {
        toast.error("The CSV file is empty.");
        return;
      }

      const contactsToImport = rows
        .map((row) => mapCsvRowToContact(row))
        .filter(Boolean);

      const skippedCount = rows.length - contactsToImport.length;
      if (!contactsToImport.length) {
        toast.error(
          "No valid rows found. Required columns: Group Name, Contact Name, Contact Email."
        );
        return;
      }

      // Dedup against existing contacts and against earlier rows in this batch.
      // Match is case-insensitive on either group_name or contact_email.
      const norm = (v) => String(v || "").trim().toLowerCase();
      const existingGroupNames = new Set(
        contacts.map((c) => norm(c.group_name)).filter(Boolean)
      );
      const existingEmails = new Set(
        contacts.map((c) => norm(c.contact_email)).filter(Boolean)
      );
      const batchGroupNames = new Set();
      const batchEmails = new Set();
      const duplicateNames = [];
      const nonDuplicates = contactsToImport.filter((c) => {
        const gn = norm(c.group_name);
        const em = norm(c.contact_email);
        const isDup =
          (gn && (existingGroupNames.has(gn) || batchGroupNames.has(gn))) ||
          (em && (existingEmails.has(em) || batchEmails.has(em)));
        if (isDup) {
          duplicateNames.push(c.group_name || c.contact_email);
          return false;
        }
        if (gn) batchGroupNames.add(gn);
        if (em) batchEmails.add(em);
        return true;
      });
      const duplicateCount = duplicateNames.length;
      if (duplicateCount > 0) {
        console.warn("Skipped duplicate contacts:", duplicateNames);
      }

      if (!nonDuplicates.length) {
        toast.warn(
          `All ${duplicateCount} row${duplicateCount === 1 ? "" : "s"} were duplicates of existing contacts. Nothing imported.`
        );
        return;
      }

      const saveResults = await Promise.allSettled(
        nonDuplicates.map((contact) => saveGroupContact(contact))
      );

      const successCount = saveResults.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const failedCount = saveResults.length - successCount;

      if (successCount > 0) {
        toast.success(
          `Imported ${successCount} contact${successCount === 1 ? "" : "s"}.`
        );
      }
      if (skippedCount > 0) {
        toast.warn(
          `Skipped ${skippedCount} invalid row${skippedCount === 1 ? "" : "s"}.`
        );
      }
      if (duplicateCount > 0) {
        toast.warn(
          `Skipped ${duplicateCount} duplicate${duplicateCount === 1 ? "" : "s"} (matching group name or email).`
        );
      }
      if (failedCount > 0) {
        toast.error(
          `${failedCount} row${failedCount === 1 ? "" : "s"} failed while saving.`
        );
      }

      if (successCount > 0) {
        refreshTableHandler();
      }
    } catch (error) {
      console.error("Error importing contacts CSV:", error);
      toast.error("Unable to import CSV. Please check file format and try again.");
    } finally {
      setShowSpinner(false);
    }
  };

  return (
    <div className="container p-0">
      <h1 className="page-title">
        Marketing Contacts{" "}
        <Badge
          pill
          bg="danger"
          className="bg-warning text-white px-3 ml-3 font-weight-normal"
        >
          {searchTerm
            ? `${filteredContacts.length} of ${totalContacts}`
            : totalContacts}
        </Badge>
      </h1>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="dashboard-box text-center">
            <Card.Body>
              <h3 className="text-primary mb-1">{totalContacts}</h3>
              <p className="mb-0 text-muted">Total Contacts</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="dashboard-box text-center">
            <Card.Body>
              <h3 className="text-success mb-1">{emailsSent}</h3>
              <p className="mb-0 text-muted">Emails Sent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="dashboard-box text-center">
            <Card.Body>
              <h3 className="text-warning mb-1">{emailsPending}</h3>
              <p className="mb-0 text-muted">Emails Pending</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="dashboard-box text-center">
            <Card.Body>
              <h3 className="text-danger mb-1">{emailProblems}</h3>
              <p className="mb-0 text-muted">Email Problems</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="dashboard-wrapper">
        <Row>
          <Col md={12}>
            <div className="d-flex flex-end justify-content-between align-items-center mb-0 px-3 pt-0">
              <div className="d-flex align-items-center mb-0" style={{ gap: "12px" }}>
                <div className="search mb-0" style={{ width: "320px" }}>
                  <div className="form-group with-icon mb-0">
                    <span className="fa-icon fas fa-search"></span>
                    <Form.Control
                      type="text"
                      placeholder="Search by group, name, email, or notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="inputclass form-control"
                    />
                  </div>
                </div>
                <div className="form-group mb-0" style={{ width: "300px" }}>
                  <Form.Control
                    as="select"
                    value={findMatchingPresetId(sortKeys)}
                    onChange={(e) => {
                      const preset = SORT_PRESETS.find(
                        (p) => p.id === e.target.value
                      );
                      if (preset) setSortKeys(preset.keys);
                    }}
                    className="inputclass form-control"
                    aria-label="Sort contacts"
                  >
                    {!findMatchingPresetId(sortKeys) && (
                      <option value="">Custom sort</option>
                    )}
                    {SORT_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        Sort: {p.label}
                      </option>
                    ))}
                  </Form.Control>
                </div>

              </div>
              <div className="text-right">
                <Button
                  variant="success"
                  className="mr-2"
                  href="https://analytics.google.com/analytics/web/#/a233277852p541887774/reports/explorer?params=_u..nav%3Dmaui&collectionId=business-objectives&r=15119060529&discardConfirmed=true"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Google Analytics
                </Button>
                <Button
                  variant="outline-success"
                  className="mr-2"
                  href="https://docs.google.com/spreadsheets/d/1iSZwXlEMZAmVkhWDnm9VYuzlG4QVuCR2xZwjJvj0hSY/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  US Church Leads
                </Button>
                <Button
                  variant="success"
                  className="mr-2"
                  onClick={handleExportToExcel}
                >
                  Export to Excel
                </Button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".csv,text/csv,.xlsx"
                  style={{ display: "none" }}
                  onChange={handleCsvUpload}
                />
                <Button variant="primary" onClick={handleAddNewContact}>
                  Add New Contact
                </Button>
                <div className="mt-2">
                  <Button
                    variant="link"
                    className="p-1 text-secondary"
                    onClick={handleUploadButtonClick}
                    title="Upload Contact List"
                    aria-label="Upload Contact List"
                  >
                    <FaUpload />
                  </Button>
                  <Button
                    variant="link"
                    className="p-1 text-secondary"
                    onClick={handleDownloadCsvTemplate}
                    title="Download Contact List Template"
                    aria-label="Download Contact List Template"
                  >
                    <FaDownload />
                  </Button>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center flex-wrap px-3 pb-0">
              <span className="text-muted mr-2">Sorted by:</span>
              {sortKeys.length === 0 && (
                <span className="text-muted font-italic">None</span>
              )}
              {sortKeys.map((k, i) => {
                const meta = SORT_FIELDS[k.field];
                const label = meta ? meta.label : k.field;
                const arrow = k.dir === "desc" ? "↓" : "↑";
                return (
                  <span
                    key={`${k.field}-${i}`}
                    className="badge badge-light border mr-2 mb-1 d-inline-flex align-items-center"
                    style={{ padding: "6px 8px", fontWeight: 500 }}
                  >
                    <span className="text-muted mr-1">{i + 1}.</span>
                    {label} {arrow}
                    <button
                      type="button"
                      aria-label={`Remove ${label} sort`}
                      className="btn btn-link btn-sm p-0 ml-2"
                      style={{ lineHeight: 1, textDecoration: "none" }}
                      onClick={() =>
                        setSortKeys((prev) =>
                          prev.filter((_, idx) => idx !== i)
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}
              {!showAddSort && availableFieldsToAdd.length > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ml-2"
                  style={{ textDecoration: "none" }}
                  onClick={() => {
                    setAddSortField(availableFieldsToAdd[0][0]);
                    setAddSortDir("asc");
                    setShowAddSort(true);
                  }}
                >
                  + Add sort
                </Button>
              )}
              {showAddSort && (
                <span className="d-inline-flex align-items-center ml-2 mb-1">
                  <Form.Control
                    as="select"
                    size="sm"
                    value={addSortField}
                    onChange={(e) => setAddSortField(e.target.value)}
                    style={{ width: "auto", display: "inline-block" }}
                    className="mr-2"
                  >
                    {availableFieldsToAdd.map(([f, meta]) => (
                      <option key={f} value={f}>
                        {meta.label}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control
                    as="select"
                    size="sm"
                    value={addSortDir}
                    onChange={(e) => setAddSortDir(e.target.value)}
                    style={{ width: "auto", display: "inline-block" }}
                    className="mr-2"
                  >
                    <option value="asc">Ascending ↑</option>
                    <option value="desc">Descending ↓</option>
                  </Form.Control>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mr-1"
                    onClick={handleConfirmAddSort}
                    disabled={!addSortField}
                  >
                    Add
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    style={{ textDecoration: "none" }}
                    onClick={() => {
                      setShowAddSort(false);
                      setAddSortField("");
                    }}
                  >
                    Cancel
                  </Button>
                </span>
              )}
              {sortKeys.length > 0 && !showAddSort && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ml-2"
                  style={{ textDecoration: "none" }}
                  onClick={() => setSortKeys(DEFAULT_SORT_KEYS)}
                >
                  Reset
                </Button>
              )}
            </div>
            <div className="dashboard-box dashboard-col-bg sponsership-page manage-group-list">
              <div className="d-flex">
                <MarketingContactsListing
                  contacts={filteredContacts}
                  showSpinner={showSpinner}
                  refreshTable={refreshTableHandler}
                  onAddNewContact={listingRef}
                  sortKeys={sortKeys}
                  onHeaderSortClick={handleHeaderSortClick}
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default MarketingContacts;
